import React, { useEffect } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import { useConfiguratorContext, useConfiguratorEvent } from '@augmatica/iframe-interface-react'
import {
  setIsEditItemPanelActive,
  setIsMobileDrawerOpen,
  setSelectedToolbarTabIndex,
  setSelectedToolbarDoorSubTab,
  setSelectedToolbarRoofSubTab,
  setSelectedToolbarGarageSubTab,
  handleItemInfoModalOpen,
  setViewInside,
} from '../../store/ui/uiSlice'

import AlertBox from '../AlertBox/AlertBox'
import EditItemPanel from '../EditItemPanel/EditItemPanel'
import ItemPanel from '../ItemPanel/ItemPanel'
import OptionsPanel from '../OptionsPanel/OptionsPanel'
import RoofCladdingPanel from '../RoofCladdingPanel/RoofCladdingPanel'
import RoofKindPanel from '../RoofKindPanel/RoofKindPanel'
import SizePanel from '../SizePanel/SizePanel'
import LoftPanel from '../LoftPanel/LoftPanel'
import GaragePanel from '../GaragePanel/GaragePanel'
import DoorsPanel from '../DoorsPanel/DoorsPanel'
import SnowZonePanel from '../SnowZonePanel/SnowZonePanel'
import { getHtmlMessageForDivergentProperties } from '../../helpers/getHtmlMessageForDivergentProperties'
import { getInfoModalPayload } from '../../helpers/getInfoModalPayload'
import { hasCompleteMallisData } from '../../helpers/hasCompleteMallisData'
import { isLfcItem as checkLfcItem } from '../../helpers/isLfcItem'
import { getEditorialDataFromSelectedItem } from '../../helpers/getEditorialDataFromSelectedItem'

import { PanelHeader } from '../Heading/styles'
import * as GlobalStyled from '../styles'
import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from '../../hooks'

const Toolbar: React.FC = () => {
  const dispatch = useAppDispatch()

  const configurator = useConfiguratorContext()

  const { meta, props, items } = useAppSelector((state) => state.configResult)
  const { assetBaseUrl, content, langMarketCode, languageCode } = useAppSelector((state) => state.init)
  const { selectedItem, slotOptions } = useAppSelector((state) => state.selectedItem)
  const { erpItems, lfcItems, renderItems, filterOptions, withDivergentProperties } = useAppSelector(
    (state) => state.items
  )

  const { isEditItemPanelActive, selectedToolbarTabIndex, selectedToolbarRoofSubTab } = useAppSelector(
    (state) => state.ui
  )

  const hasAlert = (tabIndex: number): boolean => {
    switch (tabIndex) {
      case 0:
      case 1:
      case 2:
      case 6:
        return false
      case 3:
        return Boolean(
          Object.entries(withDivergentProperties?.doors ?? {}).length ||
            Object.entries(withDivergentProperties?.doubleDoors ?? {}).length
        )
      case 4:
        return Boolean(Object.entries(withDivergentProperties?.windows ?? {}).length)
      case 5:
        return Boolean(
          Object.entries(withDivergentProperties?.facadeSections ?? {}).length ||
            Object.entries(withDivergentProperties?.garageDoors ?? {}).length ||
            Object.entries(withDivergentProperties?.slidingDoors ?? {}).length
        )
      default:
        throw new Error(`Unexpeted tab index ${tabIndex} for Toolbar tab in hasAlert() in Toolbar component`)
    }
  }

  useEffect(() => {
    switch (selectedItem.item) {
      case 'door':
        dispatch(setSelectedToolbarDoorSubTab(0))
        dispatch(setSelectedToolbarTabIndex(3))
        break

      case 'double-door':
        dispatch(setSelectedToolbarDoorSubTab(1))
        dispatch(setSelectedToolbarTabIndex(3))
        break

      case 'window':
        dispatch(setSelectedToolbarTabIndex(4))
        break

      case 'garage-door':
        dispatch(setSelectedToolbarGarageSubTab(0))
        dispatch(setSelectedToolbarTabIndex(5))
        break

      case 'sliding-door':
        dispatch(setSelectedToolbarGarageSubTab(1))
        dispatch(setSelectedToolbarTabIndex(5))
        break

      case 'facade-section':
        dispatch(setSelectedToolbarGarageSubTab(2))
        dispatch(setSelectedToolbarTabIndex(5))
        break
    }
  }, [selectedItem])

  useConfiguratorEvent(
    'add-door',
    () => {
      dispatch(setSelectedToolbarTabIndex(3)) // TODO: replace index with tab lookup
    },
    [dispatch, filterOptions, renderItems, slotOptions]
  )

  useConfiguratorEvent(
    'add-window',
    () => {
      dispatch(setSelectedToolbarTabIndex(4)) // TODO: replace index with tab lookup
    },
    [dispatch, filterOptions, renderItems, slotOptions]
  )

  const isLfcItem = checkLfcItem(selectedItem as any)

  useConfiguratorEvent(
    'edit-item',
    () => {
      if (isEditItemPanelActive) return
      // A substituted mallis we have no data for cannot be configured, so don't open a panel that would be empty
      if (
        isLfcItem &&
        !hasCompleteMallisData(
          { contextId: selectedItem.props?.configData?.contextId, erpItemNumber: selectedItem.erpItemNumber },
          lfcItems,
          erpItems
        )
      ) {
        return
      }
      configurator.cameraToSelected()
      dispatch(setIsEditItemPanelActive(true))
      dispatch(setIsMobileDrawerOpen(true))
    },
    [dispatch, isEditItemPanelActive, isLfcItem, selectedItem, lfcItems, erpItems]
  )

  useConfiguratorEvent(
    'show-item-info',
    () => {
      const erpItem = erpItems.find((erpItem) => erpItem.Sku === selectedItem.erpItemNumber)

      const attributes = []

      if (erpItem) {
        if (isLfcItem) {
          // only works for single config product in configResult, equivalent to current implementation in lfc-frontend
          selectedItem.props?.configData.configResult.configProducts[0].editorial.attributes.map((attr: any) =>
            attributes.push({
              Title: attr.attributeName,
              Value: attr.attributeValue,
            })
          )
        }
        attributes.push(...erpItem.Attributes)
      }

      const editorialData = getEditorialDataFromSelectedItem(selectedItem, renderItems)

      const payload = getInfoModalPayload(editorialData, assetBaseUrl, langMarketCode, attributes, erpItem)

      dispatch(handleItemInfoModalOpen(payload))
    },
    [assetBaseUrl, dispatch, erpItems, renderItems, selectedItem]
  )

  return (
    <Styled.Toolbar
      hasSelectedTab={selectedToolbarTabIndex !== undefined}
      isEditItemPanelActive={isEditItemPanelActive}
      data-testid={`toolbar-${isEditItemPanelActive ? 'edit-item-active' : 'edit-item-inactive'}`}
    >
      {meta && props && items ? (
        <>
          {isEditItemPanelActive && isLfcItem && <EditItemPanel />}
          <Tabs
            selectedIndex={selectedToolbarTabIndex}
            onSelect={(index) => {
              dispatch(setSelectedToolbarTabIndex(index))
              if (index === 1) {
                configurator.setCameraView('roof')
              }
            }}
          >
            <Styled.TabsContainer>
              <TabList>
                {content?.tabs?.map((tab: any, index: number) => (
                  <Tab
                    tabIndex={index.toString()}
                    key={tab.title + index}
                    onClick={() =>
                      tab.title === 'Loft' ? dispatch(setViewInside(true)) : dispatch(setViewInside(false))
                    }
                    data-testid={`toolbar-tab-${tab.title.toLowerCase()}`}
                  >
                    <img title={tab.title} alt={tab.title} src={`${assetBaseUrl}${tab.image}`} />
                    <span>{content?.tabs?.[index]?.titleShort ?? content?.tabs?.[index]?.title}</span>
                    {hasAlert(index) && <i className="fas fa-exclamation" aria-hidden="true" />}
                  </Tab>
                ))}
              </TabList>
            </Styled.TabsContainer>

            <TabPanel>
              <PanelHeader>{content?.tabs?.[0]?.title}</PanelHeader>
              <SizePanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content?.tabs?.[1]?.title}</PanelHeader>
              <Tabs
                selectedIndex={selectedToolbarRoofSubTab}
                onSelect={(index) => {
                  dispatch(setSelectedToolbarRoofSubTab(index))
                }}
                data-testid="roof-panel-tabs"
              >
                <GlobalStyled.SubTabsList>
                  <Tab tabIndex="1" data-testid="roof-kind-tab">
                    {content?.props?.roofKind?.title}
                  </Tab>
                  <Tab tabIndex="2">{content?.props?.roofCladding?.title}</Tab>
                  <Tab tabIndex="3">{content?.props?.snowZone?.title}</Tab>
                </GlobalStyled.SubTabsList>
                <TabPanel>
                  <RoofKindPanel />
                </TabPanel>
                <TabPanel>
                  <RoofCladdingPanel />
                </TabPanel>
                <TabPanel>
                  <SnowZonePanel />
                </TabPanel>
              </Tabs>
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content?.tabs?.[2]?.title}</PanelHeader>
              <LoftPanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content?.tabs?.[3]?.title}</PanelHeader>
              {hasAlert(3) && (
                <AlertBox
                  title={content?.other?.divergentPropertiesTitle}
                  htmlMessage={getHtmlMessageForDivergentProperties(
                    {
                      doors: withDivergentProperties?.doors,
                      doubleDoors: withDivergentProperties?.doubleDoors,
                    },
                    content,
                    lfcItems,
                    renderItems,
                    languageCode
                  )}
                />
              )}
              <DoorsPanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content?.tabs?.[4]?.title}</PanelHeader>
              {hasAlert(4) && (
                <AlertBox
                  title={content?.other?.divergentPropertiesTitle}
                  htmlMessage={getHtmlMessageForDivergentProperties(
                    {
                      windows: withDivergentProperties?.windows,
                    },
                    content,
                    lfcItems,
                    renderItems,
                    languageCode
                  )}
                />
              )}
              <ItemPanel itemType="windows" />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content?.tabs?.[5]?.title}</PanelHeader>
              {hasAlert(5) && (
                <AlertBox
                  title={content?.other?.divergentPropertiesTitle}
                  htmlMessage={getHtmlMessageForDivergentProperties(
                    {
                      facadeSections: withDivergentProperties?.facadeSections,
                      garageDoors: withDivergentProperties?.garageDoors,
                      slidingDoors: withDivergentProperties?.slidingDoors,
                    },
                    content,
                    lfcItems,
                    renderItems,
                    languageCode
                  )}
                />
              )}
              <GaragePanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content?.tabs?.[6]?.title}</PanelHeader>
              <OptionsPanel />
            </TabPanel>
          </Tabs>
        </>
      ) : window.innerWidth < 1200 ? (
        <div style={{ padding: '0 15px' }}>
          <div style={{ height: '90px' }} />
          <Skeleton width="100%%" height="55px" />
        </div>
      ) : (
        <Skeleton count={5} width="90%" height="70px" />
      )}
    </Styled.Toolbar>
  )
}

export default Toolbar
